import { AuthError, fetchAuthSession, getCurrentUser, signIn, signOut } from '@aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { LoginPayload } from '@redux/oauth/oauthTypes';

export type IdTokenAndExpiration = {
  idToken: string | undefined;
  idTokenExpire: number | undefined;
};

export type OAuthUserData = {
  idToken: string;
  email: string;
  cognitoSub: string;
  emailVerified: boolean;
  givenName?: string;
  familyName?: string;
  name?: string;
  providerName?: string;
  providerId?: string;
};

export const amplifyFetchIdToken = async (forceRefresh: boolean = false, errorFunc?: (error: AuthError) => void): Promise<IdTokenAndExpiration | null> => {
  try {
    const session = await fetchAuthSession({ forceRefresh });
    const idToken = session?.tokens?.idToken;
    const idTokenExpire = idToken?.payload?.exp;
    console.log('amplifyFetchIdToken, forceRefresh', forceRefresh, 'idToken', idToken?.toString());
    console.log('idTokenExpire', idTokenExpire);
    return {
      idToken: idToken?.toString(),
      idTokenExpire
    };
  } catch (error) {
    console.error('Error fetching ID token:', error);
    errorFunc?.(error as AuthError);
    return null;
  }
};

export const amplifySignIn = async (loginPayload: LoginPayload, errorFunc?: (error: AuthError) => void): Promise<void> => {
  try {
    await signIn(loginPayload);
  } catch (error) {
    if ((error as AuthError).name === 'UserAlreadyAuthenticatedException') {
      try {
        await signOut(); // signOut from cognito if user already authenticated
        await signIn(loginPayload); // try to sign in again
      } catch (signOutError) {
        console.error(signOutError);
        errorFunc?.(signOutError as AuthError);
      }
      return;
    }
    console.log(error);
    errorFunc?.(error as AuthError);
  }
};

export const amplifyConfigure = (deploymentEnv: string): void => {
  let awsConfig;
  console.log('amplifyConfigure', deploymentEnv);
  deploymentEnv = deploymentEnv.toLowerCase();
  if (deploymentEnv === 'local') {
    awsConfig = require('./amplify-config-local.json');
  } else if (deploymentEnv === 'develop') {
    awsConfig = require('./amplify-config-develop.json');
  } else if (deploymentEnv === 'staging') {
    awsConfig = require('./amplify-config-staging.json');
  } else {
    awsConfig = require('./amplify-config-prod.json');
  }
  Amplify.configure(awsConfig);
};

export const amplifyGetOAuthUserData = async (errorFunc?: (error: AuthError) => void): Promise<OAuthUserData | null> => {
  try {
    const user = await getCurrentUser();
    if (!user) {
      return null;
    }

    const session = await fetchAuthSession();
    const idToken = session?.tokens?.idToken;
    const idTokenString = idToken?.toString();

    if (!idToken || !idTokenString) {
      return null;
    }

    // Extract user info from idToken payload
    const payload = idToken.payload;
    const cognitoSub = payload.sub as string;
    const email = payload.email as string;
    const emailVerified = payload.email_verified as boolean;
    const givenName = payload.given_name as string | undefined;
    const familyName = payload.family_name as string | undefined;
    const name = payload.name as string | undefined;

    // Extract provider info from identities claim
    const identities = payload.identities as any;
    let providerName: string | undefined;
    let providerId: string | undefined;

    if (identities && Array.isArray(identities) && identities.length > 0) {
      providerName = identities[0].providerName;
      providerId = identities[0].userId;
    }

    return {
      idToken: idTokenString,
      email,
      cognitoSub,
      emailVerified,
      givenName,
      familyName,
      name,
      providerName,
      providerId
    };
  } catch (error) {
    // UserUnAuthenticatedException is expected when user is not logged in - not an error
    if ((error as AuthError).name === 'UserUnAuthenticatedException') {
      console.log('No authenticated user found (expected if not returning from OAuth)');
      return null;
    }
    // Log other errors
    console.error('Error getting OAuth user data:', error);
    errorFunc?.(error as AuthError);
    return null;
  }
};
